import { useMemo, useRef } from 'react';
import { VisuallyHidden } from 'react-aria';
import { Collection, composeRenderProps } from 'react-aria-components';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { MetricLastSeenInfo } from '@gonfalon/metrics/MetricLastSeenInfo';
import type { EventActivityResponseItem } from '@gonfalon/metrics/types';
import { TextTruncator } from '@gonfalon/ui/TextTruncator';
import type { ListBoxItemProps } from '@launchpad-ui/components';
import {
  Alert,
  Header,
  ListBox,
  ListBoxItem,
  ListBoxSection,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import type { loaderData } from '../EventActivityFormField/loaderData';

import { EventActivityResultsSidePanel } from './EventActivityResultsSidePanel';
import { EventActivitySearchResultSkeleton } from './EventActivitySearchResultSkeleton';

import styles from './EventActivitySearchResults.module.css';

export interface EventOption {
  id: string;
  index: number;
  name: string;
  description: string;
  event: EventActivityResponseItem;
}

export type EventListBoxItemProps = Array<EventOption | (typeof loaderData)[number]>;

export const SEARCH_RESULT_DISPLAY_NUMBER = 15;

export function EventActivitySearchResults({ isError, isWarehouseDataSource = false }: Props) {
  const emptyState = useMemo(() => {
    if (isError) {
      return (
        <Alert className={styles.noResultsContainer} variant="inline" status="error">
          <Message id="metrics.eventKeyField.loadError" />
        </Alert>
      );
    }
    return (
      <span className={styles.noResultsContainer}>
        <Icon name="circle-dashed" size="small" />
        <span className={styles.noResultsText}>
          <p>
            {isWarehouseDataSource ? (
              <Message id="metrics.eventKeyField.noEventsFromSource" />
            ) : (
              <Message id="metrics.eventKeyField.noEventsWithKey" />
            )}
          </p>
          <p className={styles.noResultsHint}>
            {isWarehouseDataSource ? (
              <Message id="metrics.eventKeyField.warehousePollingHint" />
            ) : (
              <Message id="metrics.eventKeyField.addKeyHint" />
            )}
          </p>
        </span>
      </span>
    );
  }, [isError, isWarehouseDataSource]);
  return (
    <ListBox
      className={styles.eventsList}
      selectionMode="single"
      aria-label={msg('metrics.eventKeyField.resultsLabel')}
      id="event-activity-list"
      renderEmptyState={() => emptyState}
    >
      {(section: { name: string; children: EventListBoxItemProps }) => (
        <ListBoxSection id={section.name}>
          <Header>{section.name}</Header>
          <Collection items={section.children}>
            {(item) => {
              if (isLoaderData(item)) {
                return (
                  <EventListBoxItem isDisabled id={item.index} textValue={item.id}>
                    <Text slot="label">
                      <EventActivitySearchResultSkeleton />
                    </Text>
                  </EventListBoxItem>
                );
              }
              return (
                // Identify each item by index so when a user selects a given item,
                // we can read the query's cached data and just extract the item by
                // its index. This will allow us to avoid doing any (de)serialization
                // on each selection (see parant component for implementation)
                <EventListBoxItem id={item.index} textValue={item.id}>
                  <Text slot="label">
                    <div className={styles.eventItemContainer}>
                      <TextTruncator>{item.name}</TextTruncator>
                      <MetricLastSeenInfo
                        lastSeen={item.event.lastSeen}
                        environmentName={item.event.environment.name}
                        hideTooltip
                      />
                    </div>
                  </Text>
                  <VisuallyHidden>
                    <Text slot="description">{item.description}</Text>
                  </VisuallyHidden>
                </EventListBoxItem>
              );
            }}
          </Collection>
        </ListBoxSection>
      )}
    </ListBox>
  );
}

interface Props {
  isError: boolean;
  isWarehouseDataSource?: boolean;
}

function isLoaderData(item: (typeof loaderData)[number] | EventOption): item is (typeof loaderData)[number] {
  if (item.event === null) {
    return true;
  }
  return false;
}

const EventListBoxItem = (props: ListBoxItemProps<EventListBoxItemProps[number]>) => {
  const itemRef = useRef(null);
  const innerContent = props?.value?.event ? (
    <EventActivityResultsSidePanel event={props.value.event} />
  ) : (
    props?.value?.description
  );

  return (
    <ListBoxItem ref={itemRef} {...props}>
      {composeRenderProps(props.children, (children, { isFocused }) => (
        <>
          {children}
          <TooltipTrigger isOpen={isFocused}>
            <Tooltip triggerRef={itemRef} placement="right" className={styles.tooltip}>
              {innerContent}
            </Tooltip>
          </TooltipTrigger>
        </>
      ))}
    </ListBoxItem>
  );
};
